import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import md5 from "md5";
import * as Sfx from "redux-saga/effects";

import {LOGOUT_FINISHED, LOGIN_FINISHED} from "@atg-global-shared/user/userActionTypes";
import * as userSelectors from "@atg-global-shared/user/userSelectors";
import {AuthActions, LOGIN_NORMAL} from "@atg-shared/auth";
import log, {serializeError} from "@atg-shared/log";
import {ModalActions} from "@atg-shared/modals-data-access";
import {
    getKambiWidgetApi,
    getKambiState,
    KambiWidgetApiRequestType,
} from "@atg-sport-shared/kambi-widget-api";
import {fetchAuthCode} from "@atg-sport-shared/sportsbook-data-access-fetch-auth-code";
import {
    kambiLoginFailed,
    SB_RESET_KAMBI_AUTH,
} from "@atg-sport-shared/sportsbook-data-access-kambi";
import {selectKambiDetails} from "@atg-sport-shared/sportsbook-data-access-kambi-details";
import {LOGIN_FAILED} from "@atg-sport-shared/sportsbook-types-kambi";

import type {GlobalUserState} from "@atg-global-shared/user";
import type {LoginFinishedAction} from "@atg-global-shared/user/userActions";
import type {KambiWidgetApi} from "@atg-sport-shared/kambi-widget-api";
import type {KambiActions} from "@atg-sport-shared/sportsbook-data-access-kambi";
import type {SagaIterator} from "redux-saga";

dayjs.extend(timezone);

function* kambiLogin(
    userId: GlobalUserState["user"]["userId"],
    forceRelogin = false,
): SagaIterator<void> {
    if (!userId) {
        return;
    }

    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    if (forceRelogin) {
        wapi.request(wapi.LOGOUT);
    } else {
        const isKambiLoggedIn = yield Sfx.call(
            getKambiState,
            KambiWidgetApiRequestType.USER_LOGGED_IN,
        );

        if (isKambiLoggedIn) {
            return;
        }
    }

    try {
        const response = yield Sfx.call(fetchAuthCode);
        const {errorCode, launchData: ticket} = response.data || {};

        if (errorCode) {
            yield Sfx.put(kambiLoginFailed(LOGIN_FAILED.ATG_ERROR));
            return;
        }

        if (!ticket?.length) {
            wapi.set(wapi.BETSLIP_HIDE);

            yield Sfx.put(
                ModalActions.showKambiModal({
                    status: "error",
                    variant: "geolocation-error",
                }),
            );

            return;
        }

        const {market, currencyCode, locale} = yield Sfx.select(selectKambiDetails);

        const payload = {
            market,
            currency: currencyCode,
            locale,
            punterId: md5(userId),
            ticket,
        };
        wapi.request(wapi.LOGIN, payload);
    } catch (error: unknown) {
        log.error("kambi-login-error", {error: serializeError(error)});
    }
}

function* loginFinished(action: LoginFinishedAction): SagaIterator<void> {
    const {userId} = action.payload.loginData.user;
    yield Sfx.call(kambiLogin, userId);
}

function* authSetRoles(action: AuthActions.AuthSetRolesAction): SagaIterator<void> {
    // also make this logout when logged out?
    if (action.roles.includes(LOGIN_NORMAL)) {
        const userId = yield Sfx.select(userSelectors.getUsername);
        yield Sfx.call(kambiLogin, userId);
    }
}

function* authCheckResponse(
    action: AuthActions.CheckAuthSuccessAction | AuthActions.CheckAuthErrorAction,
): SagaIterator<void> {
    // TODO: Should we logout when LOGIN_NORMAL role is missing?
    if (!("error" in action) && action.payload.roles.includes(LOGIN_NORMAL)) {
        yield Sfx.call(kambiLogin, action.payload.username);
    }
}

function* resetKambiAuth(action: KambiActions): SagaIterator<void> {
    if (action.type !== SB_RESET_KAMBI_AUTH || action.reason !== "kyc-modal-close") {
        return;
    }

    const userId = yield Sfx.select(userSelectors.getUsername);
    yield Sfx.call(kambiLogin, userId, true);
}

function* login(
    action:
        | LoginFinishedAction
        | AuthActions.AuthSetRolesAction
        | AuthActions.CheckAuthSuccessAction
        | AuthActions.CheckAuthErrorAction,
): SagaIterator<void> {
    switch (action.type) {
        case LOGIN_FINISHED:
            yield Sfx.call(loginFinished, action);
            break;
        case AuthActions.AUTH_CHECK_RESPONSE:
            yield Sfx.call(authCheckResponse, action);
            break;
        case AuthActions.AUTH_SET_ROLES:
            yield Sfx.call(authSetRoles, action);
            break;
        default:
            break;
    }
}

function* logout(): SagaIterator<void> {
    const wapi = yield Sfx.call(getKambiWidgetApi);
    wapi.request(wapi.LOGOUT);
}

export function* kambiLoginSaga(): Generator<Sfx.Effect, void, void> {
    // TODO: Should we handle authentication/ACCESS_TOKEN_SUCCESS as well?
    yield Sfx.takeLeading(
        [LOGIN_FINISHED, AuthActions.AUTH_CHECK_RESPONSE, AuthActions.AUTH_SET_ROLES],
        login,
    );

    yield Sfx.takeLatest(SB_RESET_KAMBI_AUTH, resetKambiAuth);

    yield Sfx.takeLeading(LOGOUT_FINISHED, logout);
}
